import { LinePath } from '@visx/shape';
import { useTranslation } from 'react-i18next';
import type { SecondBucket } from '../perSecond';
import type { LoadStep } from '../useLineQuality';
import { axisMax, LegendItem, TimeChart, type TimeAxis } from './TimeChart';
import { latest } from './values';

/** The rate the load aimed at in `second`: the last step at or before it. */
export function targetAt(steps: LoadStep[], second: number): number {
    let kbps = 0;
    for (const s of steps) {
        if (s.second > second) break;
        kbps = s.kbps;
    }
    return kbps;
}

/**
 * The upload load on its target: the rate aimed at, sent and received by the server, as three
 * lines, so a line that cannot carry the chosen tier shows at a glance. A second without a server
 * report is a gap in the sent and received lines, not a drop to zero.
 */
export const UploadChart = ({ slots, axis, steps }: { slots: SecondBucket[]; axis: TimeAxis; steps: LoadStep[] }) => {
    const { t } = useTranslation();
    const points = slots.map((b) => ({ second: b.second, target: targetAt(steps, b.second), sent: b.loadSentKbps, received: b.loadReceivedKbps }));
    const last = latest(points, (p) => p.sent !== null);
    const yMax = axisMax(
        points.flatMap((p) => [p.target, p.sent ?? 0, p.received ?? 0]),
        1000
    );
    const mid = (p: { second: number }) => p.second + axis.slot / 2;

    return (
        <TimeChart
            title={t('TestSuite.Line.Chart.Upload.Title')}
            ariaLabel={t('TestSuite.Line.Chart.Upload.Aria', { target: last?.target ?? 0, sent: last?.sent ?? 0, received: last?.received ?? 0 })}
            legend={
                <>
                    <LegendItem swatch="bg-gray-500" label={t('TestSuite.Line.Legend.Target')} />
                    <LegendItem swatch="bg-primary" label={t('TestSuite.Line.Legend.Sent')} />
                    <LegendItem swatch="bg-success" label={t('TestSuite.Line.Legend.Received')} />
                </>
            }
            axis={axis}
            yMax={yMax}
            unit="kbit/s"
        >
            {({ x, y }) => (
                <>
                    <LinePath data={points} x={(p) => x(mid(p))} y={(p) => y(p.target)} strokeWidth={1.5} strokeDasharray="4 3" className="stroke-gray-500 fill-none" />
                    <LinePath data={points} defined={(p) => p.sent !== null} x={(p) => x(mid(p))} y={(p) => y(p.sent ?? 0)} strokeWidth={1.5} className="stroke-primary fill-none" />
                    <LinePath data={points} defined={(p) => p.received !== null} x={(p) => x(mid(p))} y={(p) => y(p.received ?? 0)} strokeWidth={1.5} className="stroke-success fill-none" />
                </>
            )}
        </TimeChart>
    );
};
